import { useState } from "react";
import { Download, Square } from "lucide-react";
import { More } from "components/field/radarUi";
import { useLabReadings } from "hooks/useRadarLab";
import { radarLabApi } from "services/radarLabApi";
import { fieldTime } from "utils/fieldFormat";
import {
  describeWaterEventReason,
  describeWaterEventWarning,
  waterEventStateInfo,
} from "utils/waterEvents";
import {
  bandOf,
  bandText,
  ceilingClampText,
  countdownText,
  currentStep,
  doubtText,
  finiteNumber,
  flagText,
  formatCm,
  formatSignedCm,
  isTargetPlan,
  levelSourceText,
  minutesText,
  reasonAdvice,
  reasonText,
  rulerStepText,
  runStateInfo,
  stepStateText,
  stepText,
  targetOutcome,
  targetStepText,
} from "utils/radarLab";
import { ConfirmAction, EmergencyPumpStop } from "./LabActions";
import LabCaptures from "./LabCaptures";
import { RunGroundTruth } from "./LabGroundTruth";
import LabLevelChart from "./LabLevelChart";

const when = (value) => (value ? `${fieldTime(value)} SGT` : "—");
const MAPPING_SOURCES = {
  greenhouse_run: "an Experiment run's logged levels",
  lab_run: "an earlier lab run",
  ruler: "a ruler reading",
};

// One water event, in the Experiment page's own words.
function WaterEventCell({ step }) {
  if (!step.water_event_state) return <span>—</span>;
  const info = waterEventStateInfo(step.water_event_state);
  const reason = describeWaterEventReason(step.water_event_skip_reason || "");
  const warnings = describeWaterEventWarning(step.water_event_warning || "");
  return (
    <span>
      {info.label}
      {reason && <small>{reason}</small>}
      {warnings.map((w) => (
        <small key={w}>{w}</small>
      ))}
    </span>
  );
}

// What a settled level rests on: what decided it (the ruler camera, the
// estimators that agreed, or one alone) and whether its window was doubtful.
function MeasuredCell({ step }) {
  if (finiteNumber(step.level_cm) === null) {
    const undecided = step.level_detail?.undecided;
    return undecided ? (
      <span>
        —<small>{undecided}</small>
      </span>
    ) : (
      <span>—</span>
    );
  }
  const source = levelSourceText(step);
  const doubts = step.low_confidence ? doubtText(step.level_detail?.low_confidence) : "";
  const jumps = (step.level_detail?.pumping_jumps || []).length;
  return (
    <span>
      {formatCm(step.level_cm, 2)}
      {finiteNumber(step.level_error_cm) !== null && (
        <small>{formatSignedCm(step.level_error_cm, 2)} from the plan</small>
      )}
      {source && <small>{source}</small>}
      {doubts && <small>Doubtful window: {doubts}</small>}
      {jumps > 0 && (
        <small>
          {jumps} estimate{jumps === 1 ? "" : "s"} jumped while it pumped
        </small>
      )}
    </span>
  );
}

// A target step's level against its target: reached, how far off and whether
// that is a hit (within the run's tolerance), on target already, or a fill
// held short of it under the tank's ceiling.
function TargetCell({ step, limits }) {
  const outcome = targetOutcome(step, limits?.target_tolerance_cm);
  if (!outcome) return <span>—</span>;
  const clamp = ceilingClampText(step, limits?.tank_ceiling_cm);
  return (
    <span>
      {formatCm(step.target_cm, 2)}
      {(outcome.kind === "hit" || outcome.kind === "missed") && (
        <small className={`field-lab-target is-${outcome.kind}`}>
          Reached {formatCm(outcome.reached, 2)}, {outcome.text}
        </small>
      )}
      {outcome.kind === "on_target" && <small>{outcome.text}</small>}
      {clamp && <small>{clamp}</small>}
    </span>
  );
}

// What a step moves: its command (a target step's, worked out when it is due).
function MoveCell({ step, target }) {
  if (target && step.level_source === "on_target") return <span>No pumping</span>;
  return (
    <span>
      <span className="field-lab-nowrap">{stepText(step)}</span>
      {target && step.state === "pending" && <small>Planned: commanded when due, from the level reached</small>}
    </span>
  );
}

function StepsTable({ run, current, limits }) {
  const steps = run.steps || [];
  const target = isTargetPlan(run) || steps.some((s) => finiteNumber(s.target_cm) !== null);
  const photos = Object.fromEntries((run.ruler?.photos || []).map((p) => [p.image_id, p]));
  return (
    <div className="field-lab-scroll">
      <table className="field-table field-lab-steps">
        <thead>
          <tr>
            <th scope="col">Step</th>
            <th scope="col">Move</th>
            {target && <th scope="col">Target</th>}
            <th scope="col">Planned level</th>
            <th scope="col">Lab step</th>
            <th scope="col">Pump operation</th>
            <th scope="col">Pumped</th>
            <th scope="col">Measured</th>
            <th scope="col">Ruler camera</th>
            <th scope="col">Photos</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((step, i) => (
            <tr key={step.seq ?? i} className={current?.index === i && run.active ? "is-current" : ""}>
              <td>
                {i + 1} of {steps.length}
              </td>
              <td>
                <MoveCell step={step} target={target} />
              </td>
              {target && (
                <td>
                  <TargetCell step={step} limits={limits} />
                </td>
              )}
              <td>
                {formatCm(step.planned_before_cm, target ? 2 : 1)} → {formatCm(step.planned_after_cm, target ? 2 : 1)}
              </td>
              <td>
                {stepStateText(step.state)}
                {step.retries ? <small>Retried {step.retries}×</small> : null}
                {step.reason_text && <small>{step.reason_text}</small>}
              </td>
              <td>
                <WaterEventCell step={step} />
              </td>
              <td>
                {finiteNumber(step.pump_elapsed_ms) !== null
                  ? `${(step.pump_elapsed_ms / 1000).toFixed(1)} s`
                  : "—"}
                {step.pump_stop_reason && step.pump_stop_reason !== "completed" && (
                  <small>Stopped: {step.pump_stop_reason}</small>
                )}
              </td>
              <td>
                <MeasuredCell step={step} />
              </td>
              <td>
                <RulerCell step={step} photos={photos} min={run.ruler?.confidence_min} />
              </td>
              <td>
                {step.photo_before_id || step.photo_after_id
                  ? `${step.photo_before_id ? `before #${step.photo_before_id}` : ""}${step.photo_before_id && step.photo_after_id ? ", " : ""}${step.photo_after_id ? `after #${step.photo_after_id}` : ""}`
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RulerCell({ step, photos, min }) {
  const { text, detail } = rulerStepText(step, photos, min);
  return (
    <span>
      {text}
      {detail && <small>{detail}</small>}
    </span>
  );
}

// A ruler reading before the lab run's first step (while it prepares the node or
// records the baseline): the new reference for its level checks. The server
// refuses one later; stopping and starting a new lab run re-measures.
const RULER_STATES = ["arming", "baseline"];
function RulerOverride({ run, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const value = finiteNumber(text.replace(",", "."));
  const valid = value !== null && value >= 0 && value <= 60;
  const send = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await radarLabApi.setLevel(run.id, value);
      setMessage(`Ruler reading ${formatCm(value)} is now the reference.`);
      setText("");
      onChanged?.();
    } catch (e) {
      setError(e.message || "The ruler reading was not accepted.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="field-lab-input">
      <label htmlFor={`lab-run-ruler-${run.id}`}>Ruler reading before the first step (cm)</label>
      <div className="field-lab-row">
        <input
          id={`lab-run-ruler-${run.id}`}
          inputMode="decimal"
          value={text}
          aria-invalid={text && !valid ? "true" : "false"}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. 28.4"
        />
        <button type="button" className="field-button" disabled={!valid || busy} onClick={send}>
          {busy ? "Sending…" : "Use this ruler reading"}
        </button>
      </div>
      {error && (
        <div className="field-alert" role="alert" style={{ marginTop: 8, marginBottom: 0 }}>
          {error}
        </div>
      )}
      {message && (
        <small className="field-caption" role="status" style={{ marginTop: 0 }}>
          {message}
        </small>
      )}
    </div>
  );
}

// A lab run: where it is, what to do about it, every step in the Experiment
// page's words, the level chart, the radar captures and the export.
export default function LabRunPanel({ run, error, now, limits, onChanged }) {
  const active = !!run?.active;
  const readings = useLabReadings(run?.id ?? null, active, run?.state);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  if (!run)
    return error ? (
      <div className="field-alert" role="alert">
        {error}
      </div>
    ) : null;
  const info = runStateInfo(run.state);
  const current = currentStep(run);
  const doneSteps = (run.steps || []).filter((s) => s.state === "done").length;
  const mapping = run.mapping || null;
  const runLimits = { ...(limits || {}), ...(run.limits || {}) };
  const target = isTargetPlan(run);
  const parts = finiteNumber(run.parts) ?? 1;
  const band = bandOf(run);
  const flags = run.flags || [];
  const advice = reasonAdvice(run.reason);
  const exportRun = async () => {
    setExporting(true);
    setExportError("");
    try {
      await radarLabApi.exportRun(run.id);
    } catch (e) {
      setExportError(e.message || "The export failed.");
    } finally {
      setExporting(false);
    }
  };
  return (
    <section className="field-panel" aria-labelledby={`lab-run-${run.id}-title`}>
      <div className="field-panel-head">
        <div>
          <h2 id={`lab-run-${run.id}-title`}>
            Lab run #{run.id} · {run.label || run.preset}
          </h2>
          <p>
            {run.node?.name || run.node?.mac || "Node"} with {run.controller?.name || run.controller?.mac || "the controller"} ·
            started {when(run.armed_at || run.created_at)}
            {run.created_by ? ` by ${run.created_by}` : ""}
            {run.ended_at ? ` · ended ${when(run.ended_at)}` : ""}
          </p>
        </div>
        <span className={`field-state ${info.tone}`}>{run.state_text || info.label}</span>
      </div>
      <div className="field-panel-body field-lab">
        {error && (
          <div className="field-alert" role="alert" style={{ marginBottom: 0 }}>
            {error}
          </div>
        )}
        {run.hold && (
          <div className="field-lab-hold" role="status">
            <b>Holding since {when(run.hold.since)}:</b> {run.hold.reason_text || run.hold.reason}. No new pump step
            starts until this clears.
          </div>
        )}
        {!active && run.reason && (
          <div className={run.state === "failed" ? "field-alert" : "field-notice"} role="status" style={{ marginBottom: 0 }}>
            {run.state === "failed" ? "Stopped automatically: " : "Ended: "}
            {reasonText(run)}
            {advice && ` ${advice}`}
          </div>
        )}
        {active && current && (
          <div className="field-lab-now" aria-label="Current step">
            <strong>
              Step {current.index + 1} of {current.total}
            </strong>
            <span>
              {targetStepText(current.step) ? `${targetStepText(current.step)}: ` : ""}
              {stepText(current.step)} · {stepStateText(current.step.state)}
              {current.step.water_event_state ? ` · ${waterEventStateInfo(current.step.water_event_state).label}` : ""}
            </span>
            {run.next_step_at && (
              <span>Next pump step {countdownText(run.next_step_at, now)}</span>
            )}
          </div>
        )}
        {active && (
          <div className="field-lab-columns">
            <div className="field-lab-stack">
              <ConfirmAction
                label="Stop lab run"
                icon={Square}
                className="field-button danger"
                question="Stop this lab run?"
                confirmLabel="Confirm: stop the lab run"
                onConfirm={async () => {
                  await radarLabApi.stop(run.id);
                  onChanged?.();
                  return "Stop sent: no further pump step starts; the node's settings are restored.";
                }}
              >
                No further pump step starts. A step still waiting for its photo is cancelled; one already pumping
                finishes on the controller's own timer (use the emergency stop to cut it short). The node's settings
                are restored.
              </ConfirmAction>
              {RULER_STATES.includes(run.state) && <RulerOverride run={run} onChanged={onChanged} />}
            </div>
            <EmergencyPumpStop controller={run.controller} onDone={onChanged} />
          </div>
        )}
        <dl className="field-lab-facts">
          <div>
            <dt>Start level</dt>
            <dd>{formatCm(run.start_level_cm, 2)}</dd>
          </div>
          <div>
            <dt>End level</dt>
            <dd>{formatCm(run.end_level_cm, 2)}</dd>
          </div>
          <div>
            <dt>Steps done</dt>
            <dd>
              {run.steps_done ?? doneSteps} of {run.steps_total ?? (run.steps || []).length}
            </dd>
          </div>
          <div>
            <dt>Pumping</dt>
            <dd>
              {minutesText(run.pump_s_done ?? 0)} of {minutesText(run.pump_s_planned)}
            </dd>
          </div>
          <div>
            <dt>Level reference</dt>
            <dd>
              {mapping
                ? `${MAPPING_SOURCES[mapping.source] || mapping.source}${mapping.run_id ? ` (${mapping.run_id})` : ""}${finiteNumber(mapping.ruler_level_cm) !== null ? ` (${formatCm(mapping.ruler_level_cm)})` : ""}`
                : "—"}
            </dd>
          </div>
          <div>
            <dt>Photos</dt>
            <dd>{run.camera_run_id ? `Camera run ${run.camera_run_id}` : "—"}</dd>
          </div>
          <div>
            <dt>Clean</dt>
            <dd>{active ? "Not yet" : run.clean ? "Yes: it opens the next tier" : "No"}</dd>
          </div>
          {target && (
            <div>
              <dt>Targets hit</dt>
              <dd>
                {finiteNumber(run.targets_settled) !== null
                  ? `${run.targets_hit ?? 0} of ${run.targets_settled} settled within ±${finiteNumber(runLimits.target_tolerance_cm) ?? 0.06} cm`
                  : "—"}
              </dd>
            </div>
          )}
          {target && (parts > 1 || band) && (
            <div>
              <dt>{parts > 1 ? "Part" : "Band"}</dt>
              <dd>
                {parts > 1 ? `${run.part ?? 1} of ${parts}` : ""}
                {parts > 1 && band ? " · " : ""}
                {band ? `${parts > 1 ? "band " : ""}${bandText(band)}` : ""}
              </dd>
            </div>
          )}
          <div>
            <dt>Flags</dt>
            <dd>
              {flags.length ? (
                <ul className="field-lab-flags" aria-label="Flags">
                  {flags.map((flag) => (
                    <li key={flag}>{flagText(flag, runLimits)}</li>
                  ))}
                </ul>
              ) : (
                "None"
              )}
            </dd>
          </div>
        </dl>
        {run.notes && <p className="field-caption" style={{ marginTop: 0 }}>Notes: {run.notes}</p>}
        <More label={`All ${(run.steps || []).length} steps`} open={active}>
          <StepsTable run={run} current={current} limits={runLimits} />
        </More>
        <RunGroundTruth run={run} />
        <div>
          <h3 className="field-radar-heading">Level</h3>
          {readings.error && (
            <div className="field-alert" role="alert">
              {readings.error}
            </div>
          )}
          <LabLevelChart readings={readings.points} run={run} limits={run.limits || limits} />
        </div>
        <div>
          <h3 className="field-radar-heading">Radar captures</h3>
          <LabCaptures runId={run.id} active={active} refresh={run.state} />
        </div>
        <div className="field-lab-row">
          <button type="button" className="field-button" onClick={exportRun} disabled={exporting}>
            <Download size={15} /> {exporting ? "Preparing the export…" : "Export this lab run (zip)"}
          </button>
          <small className="field-caption" style={{ marginTop: 0 }}>
            Steps, readings with their labels, captures, pump events, environment and photo labels.
          </small>
        </div>
        {exportError && (
          <div className="field-alert" role="alert" style={{ marginBottom: 0 }}>
            {exportError}
          </div>
        )}
      </div>
    </section>
  );
}

